import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useQueryState, parseAsInteger, parseAsString } from "nuqs";
import { useCallback, useMemo } from "react";
import { schoolService, userService } from "../../../services";

// URL state parsers
const searchParser = parseAsString.withDefault("");
const gradeParser = parseAsString.withDefault("");
const statusParser = parseAsString.withDefault("");
const classParser = parseAsString.withDefault("");
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

// Tab lọc/hiển thị toàn bộ học sinh phía client, còn BE mặc định per_page=10 → xin đủ 1 lần.
const SCHOOL_STUDENTS_FETCH_LIMIT = 1000;

const useStudent = (schoolId?: string) => {
  const queryClient = useQueryClient();

  // URL state management với nuqs
  const [search, setSearch] = useQueryState("search", searchParser);
  const [grade, setGrade] = useQueryState("grade", gradeParser);
  const [status, setStatus] = useQueryState("status", statusParser);
  const [classFilter, setClassFilter] = useQueryState("class", classParser);
  const [page, setPage] = useQueryState("page", pageParser);
  const [per_page, setPerPage] = useQueryState("per_page", perPageParser);

  // Tạo filters object từ URL state
  const filters = useMemo(() => ({
    search: search || undefined,
    grade: grade || undefined,
    status: status || undefined,
    class: classFilter || undefined,
  }), [search, grade, status, classFilter]);

  // Query key cho caching
  const queryKey = ['school-students', schoolId];

  // Fetch students với react-query using new API
  const {
    data: studentsResponse,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: () =>
      schoolService.getSchoolStudents({ schoolId, per_page: SCHOOL_STUDENTS_FETCH_LIMIT }),
    enabled: !!schoolId, // Only run query if schoolId is provided
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút (cacheTime cũ)
  });

  // Filter students based on local filters
  const filteredStudents = useMemo(() => {
    if (!studentsResponse?.data) return [];
    
    return studentsResponse.data.filter((student: any) => {
      const matchesSearch = !filters.search ||
        (student.user?.name ?? student.name)?.toLowerCase().includes(filters.search.toLowerCase()) ||
        (student.user?.email ?? student.email)?.toLowerCase().includes(filters.search.toLowerCase()) ||
        (student.schoolUserData?.metadata?.class?.toLowerCase() || '').includes(filters.search.toLowerCase()) ||
        (student.schoolClass?.name?.toLowerCase() || '').includes(filters.search.toLowerCase());
      
      const matchesGrade = !filters.grade || 
        student.schoolUserData?.metadata?.grade === filters.grade;
      
      const matchesStatus = !filters.status || 
        student.schoolUserData?.status === filters.status;
      
      const matchesClass = !filters.class || 
        student.schoolUserData?.metadata?.class === filters.class ||
        student.schoolClass?.name === filters.class;
      
      return matchesSearch && matchesGrade && matchesStatus && matchesClass;
    });
  }, [studentsResponse?.data, filters]);

  // BE gắn học sinh vào trường qua schoolId (ghi school_users); khối/lớp/ngày nhập học lưu ở metadata
  const createStudentMutation = useMutation({
    // Tạo tài khoản không gửi lại an toàn được (dễ tạo trùng), còn retry mặc định của app treo khi tab mất focus
    retry: false,
    mutationFn: (data: any) =>
      userService.createUser({
        name: data.name,
        email: data.email,
        phone: data.phone,
        user_name: data.user_name,
        role: 'student',
        type: 'student',
        schoolId,
        metadata: {
          grade: data.grade || undefined,
          class: data.class || undefined,
          enrollmentDate: data.enrollmentDate,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
  });

  // Filter và pagination handlers
  const updateFilters = useCallback(
    (newFilters: Partial<typeof filters>) => {
      if (newFilters.search !== undefined) setSearch(newFilters.search || null);
      if (newFilters.grade !== undefined) setGrade(newFilters.grade || null);
      if (newFilters.status !== undefined) setStatus(newFilters.status || null);
      if (newFilters.class !== undefined)
        setClassFilter(newFilters.class || null);
      // Reset về trang 1 khi filter thay đổi
      setPage(1);
    },
    [setSearch, setGrade, setStatus, setClassFilter, setPage]
  );

  const updatePagination = useCallback(
    (newPage: number, newPerPage?: number) => {
      setPage(newPage);
      if (newPerPage) setPerPage(newPerPage);
    },
    [setPage, setPerPage]
  );

  const resetFilters = useCallback(() => {
    setSearch(null);
    setGrade(null);
    setStatus(null);
    setClassFilter(null);
    setPage(1);
  }, [setSearch, setGrade, setStatus, setClassFilter, setPage]);

  // Clear specific filter
  const clearFilter = useCallback((filterKey: keyof typeof filters) => {
    switch (filterKey) {
      case 'search':
        setSearch(null);
        break;
      case 'grade':
        setGrade(null);
        break;
      case 'status':
        setStatus(null);
        break;
      case 'class':
        setClassFilter(null);
        break;
    }
    setPage(1);
  }, [setSearch, setGrade, setStatus, setClassFilter, setPage]);

  return {
    // Data
    students: filteredStudents,
    pagination: {
      total: filteredStudents.length,
      current_page: page,
      per_page,
      total_pages: Math.ceil(filteredStudents.length / per_page)
    },
    
    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    refetch,
    updateFilters,
    updatePagination,
    resetFilters,
    clearFilter,

    // Mutations
    createStudent: createStudentMutation.mutate,

    // Mutation states
    isCreating: createStudentMutation.isPending,

    // Mutation objects for advanced handling
    createStudentMutation,
  };
};

export default useStudent;
